import React, { useEffect, useState } from "react";
import api from "../../services/api";
import { Link } from "react-router-dom";
import useRoles from "../../hooks/useRoles";
import MultiSelectDropdown from "../../components/MultiSelectDropdown";

const WarehouseItemList = () => {
  const { hasRole } = useRoles();

  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [totalCount, setTotalCount] = useState(0);

  const [filters, setFilters] = useState({
    namePart: "",
    categoryIds: [],
    sortParam: "article_asc",
    onlyWithCount: false,
    pageSize: 10,
    pageNumber: 1
  });

  const totalPages = Math.ceil(totalCount / filters.pageSize);

  // ---- Load categories ----
  useEffect(() => {
    const load = async () => {
      const res = await api.get("/categories");
      setCategories(res.data);
    };
    load();
  }, []);

  // ---- Load warehouse items ----
  useEffect(() => {
    const load = async () => {
      try {
        const res = await api.post("/ItemWarehouse/filter", filters);
        setItems(res.data.elements);
        setTotalCount(res.data.totalCount);
      } catch (e) {
        console.error(e);
      }
    };
    load();
  }, [filters]);

  // ---- Pagination ----
  const handlePageChange = (page) => {
    setFilters({ ...filters, pageNumber: page });
  };

  // ---- Change filters ----
  const handleFilterChange = (e) => {
    setFilters({ ...filters, [e.target.name]: e.target.value, pageNumber: 1 });
  };

  // ---- Add +1 ----
  const handleAdd = async (id) => {
    try {
      await api.patch(`/ItemWarehouse/${id}`);
      setItems(items.map(x => x.id === id ? { ...x, count: x.count + 1 } : x));
    } catch (err) {
      console.error(err);
    }
  };

  // ---- Remove -1 ----
  const handleRemove = async (id) => {
    try {
      await api.delete(`/ItemWarehouse/${id}`);
      setItems(items.map(x =>
        x.id === id ? { ...x, count: Math.max(0, x.count - 1) } : x
      ));
    } catch (err) {
      console.error(err);
    }
  };

  const getPageNumbers = (current, total) => {
    const pages = [];
    const delta = 2;

    const range = [];
    for (
      let i = Math.max(2, current - delta);
      i <= Math.min(total - 1, current + delta);
      i++
    ) {
      range.push(i);
    }

    if (current - delta > 2) range.unshift("...");
    if (current + delta < total - 1) range.push("...");

    range.unshift(1);
    if (total > 1) range.push(total);

    return range;
  };

  return (
    <div className="container mt-4">
      <h1>Склад товаров</h1>

      {/* FILTERS */}
      <div className="row mb-3">
        <div className="col-md-3">
          <input
            type="text"
            className="form-control"
            placeholder="Поиск по названию или артикулу"
            name="namePart"
            value={filters.namePart}
            onChange={handleFilterChange}
          />
        </div>

        <div className="col-md-3">
          <MultiSelectDropdown
            options={categories}
            selectedIds={filters.categoryIds}
            onChange={(newIds) =>
              setFilters({ ...filters, categoryIds: newIds, pageNumber: 1 })
            }
            placeholderAll="Все категории"
            placeholderOne="Категория"
            placeholderMany="категорий"
          />
        </div>

        <div className="col-md-3">
          <select
            className="form-control"
            name="sortParam"
            value={filters.sortParam}
            onChange={handleFilterChange}
          >
            <option value="name_desc">Название Я-А</option>
            <option value="name_asc">Название А-Я</option>
            <option value="article_asc">Артикул ↑</option>
            <option value="article_desc">Артикул ↓</option>
          </select>
        </div>

        <div className="col-md-3 d-flex align-items-center">
          <input
            type="checkbox"
            className="form-check-input me-2"
            checked={filters.onlyWithCount}
            onChange={() =>
              setFilters({
                ...filters,
                onlyWithCount: !filters.onlyWithCount,
                pageNumber: 1
              })
            }
          />
          Только с количеством
        </div>
      </div>

      {/* TABLE */}
      <table className="table table-striped">
        <thead>
          <tr>
            <th>Изображение</th>
            <th>Название</th>
            <th>Артикул</th>
            <th>Количество</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <tr key={item.id}>
              <td>
                {item.imageId ? (
                  <img
                    src={`/PngFiles/${item.imageId}.png`}
                    alt={item.name}
                    style={{
                      width: "100px",
                      height: "100px",
                      objectFit: "contain"
                    }}
                  />
                ) : (
                  <div
                    style={{ width: "100px", height: "100px", background: "#eee" }}
                  ></div>
                )}
              </td>
              <td>
                <Link to={`/items/${item.id}`}>{item.name}</Link>
              </td>
              <td>{item.article}</td>
              <td>
                <div className="d-flex justify-content-center align-items-center gap-3">

                    {hasRole("AddWarehouseItems") && (
                        <button
                        className="btn btn-outline-danger warehouse-circle-btn"
                        onClick={() => handleRemove(item.id)}
                        >
                        –
                        </button>
                    )}
                    
                    {/* Количество */}
                    <div className="warehouse-count-text">
                    <strong>{item.count}</strong>
                    </div>

                    {hasRole("RemoveWarehouseItems") && (
                        <button
                        className="btn btn-outline-success warehouse-circle-btn"
                        onClick={() => handleAdd(item.id)}
                        >
                        +
                        </button>
                    )}
                    
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* PAGINATION */}
      <nav>
        <ul className="pagination">
          <li className={`page-item ${filters.pageNumber === 1 ? "disabled" : ""}`}>
            <button
              className="page-link"
              onClick={() => handlePageChange(filters.pageNumber - 1)}
            >
              Назад
            </button>
          </li>

          {getPageNumbers(filters.pageNumber, totalPages).map((p, idx) => (
            <li
              className={`page-item ${p === filters.pageNumber ? "active" : ""} ${
                p === "..." ? "disabled" : ""
              }`}
              key={idx}
            >
              {p === "..." ? (
                <span className="page-link">...</span>
              ) : (
                <button
                  className="page-link"
                  onClick={() => handlePageChange(p)}
                >
                  {p}
                </button>
              )}
            </li>
          ))}

          <li
            className={`page-item ${
              filters.pageNumber === totalPages ? "disabled" : ""
            }`}
          >
            <button
              className="page-link"
              onClick={() => handlePageChange(filters.pageNumber + 1)}
            >
              Вперед
            </button>
          </li>
        </ul>
      </nav>
    </div>
  );
};

export default WarehouseItemList;
